import { useState } from 'react';
import { Mail, Lock, AlertCircle, CheckCircle2, ArrowRight, Sparkles, Globe2, Zap, ShieldCheck, Building2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { pwnedCount, PWNED_MESSAGE } from '../lib/pwned';
import Button from './ui/Button';
import { Input, Label } from './ui/Input';
import { LogoLockup } from './ui/Logo';
import Tabs from './ui/Tabs';

// v10: „Załóż konto” (agencje) widoczne na dev/podglądach; na produkcji dopiero po REACT_APP_SIGNUP_ENABLED=true
// w Vercelu (serwer i tak pilnuje config.agency_signup_enabled).
const PROD_HOSTS = /^(app\.mams\.com\.pl|multi-agent-marketplace-system\.mediafy\.com\.pl|multi-agent-marketplace-system\.vercel\.app)$/;
const SIGNUP_ENABLED = process.env.REACT_APP_SIGNUP_ENABLED === 'true'
  || (process.env.REACT_APP_SIGNUP_ENABLED !== 'false' && typeof window !== 'undefined' && !PROD_HOSTS.test(window.location.hostname));

export default function LoginPage({ onLogin }) {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  // v9/A3: rejestracja agencji
  const [orgName, setOrgName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPass, setSignupPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      onLogin(data.user, data.session);
    } catch (err) {
      setError('Nieprawidłowy e-mail lub hasło.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: `${window.location.origin}/?recovery=1` });
      if (error) throw error;
      setSuccess('Link wysłany (ważny 1 h). Otwórz go w tej przeglądarce — wrócisz tutaj i ustawisz nowe hasło.');
    } catch (err) {
      const m = String(err?.message || '');
      if (/rate limit|too many|security purposes/i.test(m)) setError('Za dużo prób — poczta Supabase pozwala na 2 maile resetu na godzinę. Odczekaj i spróbuj ponownie.');
      else setError(`Nie udało się wysłać linku: ${m || 'sprawdź adres e-mail'}.`);
    } finally {
      setLoading(false);
    }
  };

  // v9/A3: „Załóż konto” — konto agencji. Organizację (plan free, 20 kredytów) zakłada aplikacja po pierwszym
  // zalogowaniu (edge org-signup), na podstawie user_metadata.signup = 'agency'.
  const handleSignup = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (orgName.trim().length < 2) return setError('Podaj nazwę firmy.');
    if (signupPass.length < 8) return setError('Hasło musi mieć co najmniej 8 znaków.');
    setLoading(true);
    try {
      // Hasło z wycieków (HaveIBeenPwned, k-anonimowość) — zamiast płatnej opcji Supabase.
      if (await pwnedCount(signupPass) > 0) { setError(PWNED_MESSAGE); return; }
      const { data, error } = await supabase.auth.signUp({
        email: signupEmail.trim(),
        password: signupPass,
        options: { emailRedirectTo: window.location.origin, data: { signup: 'agency', org_name: orgName.trim() } },
      });
      if (error) throw error;
      if (data.session) { onLogin(data.user, data.session); return; }
      // Supabase zwraca „sukces” bez tożsamości, gdy e-mail już istnieje — nie zdradzamy tego, ale podpowiadamy logowanie.
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setSuccess('Jeśli to konto już istnieje — zaloguj się albo zresetuj hasło. Jeśli nie — sprawdź skrzynkę.');
      } else {
        setSuccess('Sprawdź skrzynkę i kliknij link potwierdzający — wrócisz tutaj zalogowany, a my założymy konto agencji.');
      }
    } catch (err) {
      const m = String(err?.message || '');
      if (/rate limit|too many|security purposes/i.test(m)) setError('Za dużo prób w krótkim czasie — spróbuj ponownie za kilka minut.');
      else if (/password/i.test(m)) setError(`Hasło: ${m}`);
      else setError(`Nie udało się założyć konta: ${m || 'sprawdź dane'}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT — form panel */}
      <div className="flex-1 flex flex-col justify-between bg-white dark:bg-ink-950 px-6 py-8 sm:px-12 lg:px-16">
        <div>
          <LogoLockup size="md" />
        </div>

        <div className="w-full max-w-[420px] mx-auto py-12 animate-fade-in">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-ink-900 dark:text-ink-50 tracking-tighter mb-2">
              {tab === 'login' ? 'Zaloguj się do MAMS' : tab === 'signup' ? 'Załóż konto agencji' : 'Resetuj hasło'}
            </h1>
            <p className="text-sm text-ink-500 dark:text-ink-400">
              {tab === 'login'
                ? 'Generuj oferty marketplace klasy światowej. W skali. Automatycznie.'
                : tab === 'signup'
                  ? '20 kredytów na start, bez karty. Dodajesz swoich klientów i wystawiasz ich katalogi.'
                  : 'Podaj e-mail, a wyślemy ci link do ustawienia nowego hasła.'}
            </p>
          </div>

          <Tabs
            tabs={[
              { id: 'login', label: 'Logowanie' },
              ...(SIGNUP_ENABLED ? [{ id: 'signup', label: 'Załóż konto' }] : []),
              { id: 'reset', label: 'Reset hasła' },
            ]}
            value={tab}
            onChange={(t) => { setTab(t); setError(''); setSuccess(''); }}
            className="mb-7"
          />

          {error && (
            <div className="mb-5 flex items-start gap-2.5 p-3 rounded-md bg-danger-light border border-danger/20 dark:bg-danger/15 dark:border-danger/30 animate-fade-in">
              <AlertCircle className="w-4 h-4 text-danger-dark dark:text-danger flex-shrink-0 mt-0.5" />
              <span className="text-sm text-danger-dark dark:text-danger font-medium">{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-5 flex items-start gap-2.5 p-3 rounded-md bg-success-light border border-success/20 dark:bg-success/15 dark:border-success/30 animate-fade-in">
              <CheckCircle2 className="w-4 h-4 text-success-dark dark:text-success flex-shrink-0 mt-0.5" />
              <span className="text-sm text-success-dark dark:text-success font-medium">{success}</span>
            </div>
          )}

          {tab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Adres e-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ty@firma.pl"
                  required
                  autoComplete="email"
                  leftIcon={<Mail className="w-4 h-4" />}
                />
              </div>
              <div>
                <Label htmlFor="password">Hasło</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  leftIcon={<Lock className="w-4 h-4" />}
                />
              </div>
              <Button
                type="submit"
                size="lg"
                loading={loading}
                rightIcon={!loading && <ArrowRight className="w-4 h-4" />}
                className="w-full mt-2"
              >
                {loading ? 'Logowanie…' : 'Zaloguj się'}
              </Button>
            </form>
          ) : tab === 'signup' ? (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <Label htmlFor="org-name">Nazwa firmy (agencji)</Label>
                <Input id="org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="np. Agencja Marketplace sp. z o.o." required maxLength={80} leftIcon={<Building2 className="w-4 h-4" />} />
              </div>
              <div>
                <Label htmlFor="signup-email">Adres e-mail</Label>
                <Input id="signup-email" type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} placeholder="ty@firma.pl" required autoComplete="email" leftIcon={<Mail className="w-4 h-4" />} />
              </div>
              <div>
                <Label htmlFor="signup-pass">Hasło (min. 8 znaków)</Label>
                <Input id="signup-pass" type="password" value={signupPass} onChange={(e) => setSignupPass(e.target.value)} placeholder="••••••••" required minLength={8} autoComplete="new-password" leftIcon={<Lock className="w-4 h-4" />} />
              </div>
              <Button type="submit" size="lg" loading={loading} rightIcon={!loading && <ArrowRight className="w-4 h-4" />} className="w-full mt-2">
                {loading ? 'Zakładanie…' : 'Załóż konto'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <Label htmlFor="reset-email">Adres e-mail</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="ty@firma.pl"
                  required
                  autoComplete="email"
                  leftIcon={<Mail className="w-4 h-4" />}
                />
              </div>
              <Button
                type="submit"
                size="lg"
                loading={loading}
                rightIcon={!loading && <ArrowRight className="w-4 h-4" />}
                className="w-full mt-2"
              >
                {loading ? 'Wysyłanie…' : 'Wyślij link resetujący'}
              </Button>
            </form>
          )}

          {/* Trust signals */}
          <div className="mt-10 pt-6 border-t border-ink-100 dark:border-ink-800">
            <p className="text-xs text-ink-400 dark:text-ink-500 mb-3 font-medium">Trusted by</p>
            <div className="flex items-center gap-5 text-ink-500 dark:text-ink-400">
              <span className="text-sm font-bold tracking-tighter text-ink-700 dark:text-ink-300">SPARTA</span>
              <span className="w-px h-4 bg-ink-200 dark:bg-ink-700" />
              <span className="text-xs">12,000+ produktów wygenerowanych</span>
            </div>
          </div>
        </div>

        <div className="text-2xs text-ink-400 dark:text-ink-500 font-medium tracking-wide">
          © 2026 Mediafy. All rights reserved.
        </div>
      </div>

      {/* RIGHT — visual / brand panel */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[40%] relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-800 to-brand-950">
        {/* Decorative blobs */}
        <div className="absolute -top-40 -right-32 w-[28rem] h-[28rem] bg-brand-400/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-[26rem] h-[26rem] bg-brand-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-white/5 rounded-full blur-2xl" />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center w-full px-12 xl:px-16 py-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 self-start rounded-full bg-white/10 border border-white/20 backdrop-blur">
            <Sparkles className="w-3.5 h-3.5 text-brand-200" />
            <span className="text-xs font-semibold text-white/90 tracking-tightish">Multi-Agent Marketplace System</span>
          </div>

          <h2 className="text-4xl xl:text-5xl font-bold text-white tracking-tighter leading-[1.05] mb-5 text-balance">
            One catalog.
            <br />
            <span className="text-brand-200">Eight marketplaces.</span>
            <br />
            Zero ręcznej pracy.
          </h2>

          <p className="text-base text-brand-100/80 leading-relaxed max-w-md mb-10">
            Generuj oferty produktów wielojęzyczne i SEO-optimized
            jednocześnie na Amazon, Kaufland, Allegro, eBay, Temu, Alibaba i innych. AI agents w skali tysięcy SKU.
          </p>

          {/* Flow illustration */}
          <FlowIllustration />

          {/* Feature pills */}
          <div className="mt-10 grid grid-cols-3 gap-3">
            {[
              { icon: Zap,         label: 'AI Agents',    sub: 'klasa światowa' },
              { icon: Globe2,      label: '8 Marketplace', sub: 'EU + global' },
              { icon: ShieldCheck, label: 'On-prem LLM',  sub: 'pełna kontrola' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 backdrop-blur">
                <Icon className="w-4 h-4 text-brand-200 mb-1.5" />
                <div className="text-xs font-semibold text-white">{label}</div>
                <div className="text-2xs text-brand-200/70 font-medium">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Flow illustration: catalog → MAMS → marketplaces, with subtle pulsing nodes.
 */
function FlowIllustration() {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5">
      <div className="flex items-center justify-between gap-3">
        {/* Catalog */}
        <FlowNode label="Katalog" sub="XLSX / CSV" iconColor="text-brand-200" />
        <FlowArrow />
        {/* MAMS engine */}
        <div className="flex flex-col items-center gap-2">
          <div className="relative">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-300 to-brand-500 flex items-center justify-center shadow-lg shadow-brand-900/30">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                <path d="M3 12L9 6L12 9L15 6L21 12L15 18L12 15L9 18L3 12Z" fill="white" opacity="0.95"/>
                <path d="M9 12L12 9L15 12L12 15L9 12Z" fill="rgba(37, 84, 230, 0.5)"/>
              </svg>
            </div>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-success ring-2 ring-brand-800 animate-pulse-dot" />
          </div>
          <div className="text-2xs font-bold text-white tracking-tightish">MAMS</div>
        </div>
        <FlowArrow />
        {/* Marketplaces */}
        <div className="flex flex-col gap-1.5">
          {[
            { name: 'Amazon',  bg: 'bg-[#FF9900]' },
            { name: 'Kaufland',bg: 'bg-[#E10915]' },
            { name: 'Allegro', bg: 'bg-[#FF5A00]' },
            { name: 'eBay',    bg: 'bg-[#0063D1]' },
          ].map(m => (
            <div key={m.name} className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${m.bg}`} />
              <span className="text-2xs text-white/80 font-semibold tracking-wide">{m.name}</span>
            </div>
          ))}
          <span className="text-2xs text-white/40 font-medium">+ 4 more</span>
        </div>
      </div>
    </div>
  );
}

function FlowNode({ label, sub, iconColor }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={iconColor}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M8 13h8M8 17h6" />
        </svg>
      </div>
      <div className="text-2xs font-bold text-white tracking-tightish">{label}</div>
      <div className="text-2xs text-white/50 -mt-1.5">{sub}</div>
    </div>
  );
}

function FlowArrow() {
  return (
    <svg width="32" height="14" viewBox="0 0 32 14" fill="none" className="text-brand-300/60 flex-shrink-0">
      <path d="M0 7H28M22 1L28 7L22 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 3" />
    </svg>
  );
}
